import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ScrollView, TouchableOpacity, View } from 'react-native';
import { CheckBox } from 'react-native-elements';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { firestore } from '../../../services/firebase';
import TextInput from '../../../components/TextInput';
import Button from '../../../components/Button';
import AdminSwitch from '../../../components/AdminSwitch';
import { FooterText, StandardText } from '../../../config/theme/globalStyles';

const mapSelected = (items, selectedIds = []) =>
  items.map((item) => ({
    ...item,
    selected: selectedIds.includes(item.productId),
  }));

const ChannelFormModal = ({
  visible,
  title,
  submitLabel,
  initialValue,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [onlyAdminCanPost, setOnlyAdminCanPost] = useState(false);
  const [products, setProducts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!visible) return;
    setName(initialValue?.name || '');
    setOnlyAdminCanPost(!!initialValue?.onlyAdminCanPost);
    setErrorMessage('');
  }, [visible, initialValue]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;

    const loadData = async () => {
      const initialProductIds = initialValue?.productIds || [];
      const initialPlanIds = (initialValue?.plans || []).map((plan) => plan.productId);
      const initialCourseIds = (initialValue?.coursesCard || []).map((course) => course.productId);

      const productsSnap = await getDocs(
        query(collection(firestore, 'products'))
      );
      const productsList = productsSnap.docs.map((productDoc) => ({
        id: productDoc.id,
        title: productDoc.data()?.name || productDoc.data()?.title || productDoc.data()?.productId,
        productId: productDoc.data()?.productId,
      }));

      const plansSnap = await getDocs(
        query(collection(firestore, 'plans'), orderBy('order', 'asc'))
      );
      const plansList = [];
      plansSnap.docs.forEach((planDoc) => {
        const planData = planDoc.data();
        if (planData.monthlyPriceId) {
          plansList.push({ id: `${planDoc.id}-monthly`, title: `${planData.title} Mensal`, productId: planData.productIds?.monthly });
        }
        if (planData.quarterlyPriceId || planData.quarterlyAtSightPriceId) {
          plansList.push({ id: `${planDoc.id}-quarterly`, title: `${planData.title} Trimestral`, productId: planData.productIds?.quarterly });
        }
        if (planData.semiannualPriceId || planData.semiannualAtSightPriceId) {
          plansList.push({ id: `${planDoc.id}-semiannual`, title: `${planData.title} Semestral`, productId: planData.productIds?.semiannual });
        }
        if (planData.yearlyPriceId || planData.yearlyAtSightPriceId) {
          plansList.push({ id: `${planDoc.id}-yearly`, title: `${planData.title} Anual`, productId: planData.productIds?.yearly });
        }
      });

      const coursesSnap = await getDocs(collection(firestore, 'courses'));
      const coursesList = coursesSnap.docs.map((courseDoc) => ({
        id: courseDoc.id,
        title: courseDoc.data()?.title,
        productId: courseDoc.data()?.productId || courseDoc.data()?.priceId || courseDoc.id,
      }));

      if (cancelled) return;

      setProducts(mapSelected(productsList.filter((p) => !!p.productId), initialProductIds));
      setPlans(mapSelected(plansList.filter((p) => !!p.productId), initialPlanIds));
      setCourses(mapSelected(coursesList.filter((c) => !!c.productId), initialCourseIds));
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [visible, initialValue]);

  const toggleSelected = (setter, id) => {
    setter((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const selectedPayload = useMemo(() => {
    const selectedProducts = products.filter((item) => item.selected).map((item) => item.productId);
    const selectedPlans = plans.filter((item) => item.selected).map((item) => ({ productId: item.productId }));
    const selectedCourses = courses.filter((item) => item.selected).map((item) => ({ productId: item.productId }));
    return {
      name: name.trim(),
      onlyAdminCanPost,
      productIds: selectedProducts,
      plans: selectedPlans,
      coursesCard: selectedCourses,
    };
  }, [products, plans, courses, name, onlyAdminCanPost]);

  const handleSave = async () => {
    if (!selectedPayload.name) {
      setErrorMessage('Informe o nome do canal.');
      return;
    }
    setErrorMessage('');
    setIsSaving(true);
    try {
      await onSubmit(selectedPayload);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal visible={visible} animationType="fade" transparent>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', alignItems: 'center' }}>
        <View style={{ width: '90%', maxWidth: 680, maxHeight: '90%', backgroundColor: '#FFFFFF', borderRadius: 10, padding: 16 }}>
          <StandardText color="#2e2e2e" style={{ marginBottom: 12 }}>{title}</StandardText>
          <ScrollView>
            <TextInput
              placeholder="Nome do canal"
              value={name}
              onChangeText={setName}
              backgroundColor="#FFFFFF"
              color="#2e2e2e"
            />

            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 12 }}>
              <StandardText color="#2e2e2e" style={{ marginRight: 8 }}>
                Somente admin pode postar
              </StandardText>
              <AdminSwitch value={onlyAdminCanPost} onValueChange={setOnlyAdminCanPost} />
            </View>

            {products.length > 0 && (
              <>
                <StandardText color="#2e2e2e" style={{ marginTop: 16 }}>Produtos</StandardText>
                {products.map((item) => (
                  <CheckBox
                    key={item.id}
                    title={item.title}
                    checked={!!item.selected}
                    onPress={() => toggleSelected(setProducts, item.id)}
                    containerStyle={{ backgroundColor: 'transparent', paddingVertical: 2 }}
                  />
                ))}
              </>
            )}

            {plans.length > 0 && (
              <>
                <StandardText color="#2e2e2e" style={{ marginTop: 8 }}>Planos</StandardText>
                {plans.map((item) => (
                  <CheckBox
                    key={item.id}
                    title={item.title}
                    checked={!!item.selected}
                    onPress={() => toggleSelected(setPlans, item.id)}
                    containerStyle={{ backgroundColor: 'transparent', paddingVertical: 2 }}
                  />
                ))}
              </>
            )}

            {courses.length > 0 && (
              <>
                <StandardText color="#2e2e2e" style={{ marginTop: 8 }}>Cursos</StandardText>
                {courses.map((item) => (
                  <CheckBox
                    key={item.id}
                    title={item.title}
                    checked={!!item.selected}
                    onPress={() => toggleSelected(setCourses, item.id)}
                    containerStyle={{ backgroundColor: 'transparent', paddingVertical: 2 }}
                  />
                ))}
              </>
            )}

            {!!errorMessage && (
              <FooterText color="red" style={{ marginTop: 8 }}>
                {errorMessage}
              </FooterText>
            )}
          </ScrollView>

          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 12 }}>
            <TouchableOpacity style={{ marginRight: 8 }}>
              <Button title="Cancelar" onPress={onClose} colorbutton="#FA5D5Da0" colortitle="#FFFFFF" />
            </TouchableOpacity>
            <Button title={submitLabel} onPress={handleSave} isLoading={isSaving} colorbutton="#3FC380a0" colortitle="#FFFFFF" />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default ChannelFormModal;
